import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { app } from './firebase'
import { getUserProfile } from './userProfile'
import { Difficulty } from '../hooks/gameReducer'

const db = getFirestore(app)

// Championship types
export interface ChampionshipEntry {
  userId: string
  displayName: string
  weeklyScore: number
  gamesPlayed: number
  gamesWon: number
  currentStreak: number
  lastUpdated: Timestamp
}

export interface WeekInfo {
  weekNumber: number
  startDate: Date
  endDate: Date
}

/**
 * Get current week number (since epoch)
 */
export function getCurrentWeek(): WeekInfo {
  const now = new Date()
  const startOfWeek = new Date(now)
  startOfWeek.setHours(0, 0, 0, 0)
  // Set to Monday of current week
  const day = startOfWeek.getDay()
  const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
  startOfWeek.setDate(diff)

  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(endOfWeek.getDate() + 6)
  endOfWeek.setHours(23, 59, 59, 999)

  // Calculate week number since epoch (Jan 1, 2024)
  const epoch = new Date('2024-01-01')
  const diffTime = startOfWeek.getTime() - epoch.getTime()
  const weekNumber = Math.floor(diffTime / (7 * 24 * 60 * 60 * 1000))

  return {
    weekNumber,
    startDate: startOfWeek,
    endDate: endOfWeek,
  }
}

/**
 * Calculate score based on game performance
 */
export function calculateGameScore(
  guessCount: number,
  maxGuesses: number,
  isWin: boolean
): number {
  if (!isWin) {
    return 0
  }

  // Base score for solving
  let score = 10

  // Bonus for fewer attempts (1-5 bonus points)
  const attemptBonus = Math.max(0, maxGuesses - guessCount)
  score += attemptBonus

  return score
}

/**
 * Update championship entry for a user
 */
export async function updateChampionshipScore(
  userId: string,
  scoreToAdd: number,
  isWin: boolean,
  difficulty: Difficulty = 5
): Promise<void> {
  try {
    // Get display name from user profile
    const profile = await getUserProfile(userId)
    const displayName = profile?.displayName || 'Anonim'

    const weekInfo = getCurrentWeek()
    const weekId = `week_${weekInfo.weekNumber}`
    const difficultyPath = `diff_${difficulty}`
    const entryRef = doc(db, 'championship', weekId, difficultyPath, userId)

    const entrySnap = await getDoc(entryRef)

    if (entrySnap.exists()) {
      const current = entrySnap.data() as ChampionshipEntry
      await updateDoc(entryRef, {
        displayName, // Always update display name from profile
        weeklyScore: current.weeklyScore + scoreToAdd,
        gamesPlayed: current.gamesPlayed + 1,
        gamesWon: current.gamesWon + (isWin ? 1 : 0),
        currentStreak: isWin ? current.currentStreak + 1 : 0,
        lastUpdated: serverTimestamp(),
      })
    } else {
      const newEntry: ChampionshipEntry = {
        userId,
        displayName,
        weeklyScore: scoreToAdd,
        gamesPlayed: 1,
        gamesWon: isWin ? 1 : 0,
        currentStreak: isWin ? 1 : 0,
        lastUpdated: Timestamp.now(),
      }
      await setDoc(entryRef, newEntry)
    }
  } catch (error) {
    console.error('Error updating championship score:', error)
    throw error
  }
}

/**
 * Get global championship rankings for current week
 */
export async function getChampionshipRankings(
  difficulty: Difficulty = 5
): Promise<ChampionshipEntry[]> {
  try {
    const weekInfo = getCurrentWeek()
    const weekId = `week_${weekInfo.weekNumber}`
    const difficultyPath = `diff_${difficulty}`
    const entriesRef = collection(db, 'championship', weekId, difficultyPath)
    const q = query(entriesRef, orderBy('weeklyScore', 'desc'), limit(100))

    const snapshot = await getDocs(q)
    const entries = snapshot.docs.map((doc) => doc.data() as ChampionshipEntry)

    // Sort in memory to handle ties properly
    // Higher score first, then more games played for tiebreaker
    return entries.sort((a, b) => {
      if (a.weeklyScore !== b.weeklyScore) {
        return b.weeklyScore - a.weeklyScore
      }
      return b.gamesPlayed - a.gamesPlayed
    })
  } catch (error) {
    console.error('Error getting championship rankings:', error)
    throw error
  }
}

/**
 * Get user's championship entry for current week
 */
export async function getUserChampionshipEntry(
  userId: string,
  difficulty: Difficulty = 5
): Promise<ChampionshipEntry | null> {
  try {
    const weekInfo = getCurrentWeek()
    const weekId = `week_${weekInfo.weekNumber}`
    const difficultyPath = `diff_${difficulty}`
    const entryRef = doc(db, 'championship', weekId, difficultyPath, userId)

    const entrySnap = await getDoc(entryRef)

    if (!entrySnap.exists()) {
      return null
    }

    return entrySnap.data() as ChampionshipEntry
  } catch (error) {
    console.error('Error getting user championship entry:', error)
    throw error
  }
}

/**
 * Get user's rank in current week
 */
export async function getUserRank(
  userId: string,
  difficulty: Difficulty = 5
): Promise<number | null> {
  try {
    const rankings = await getChampionshipRankings(difficulty)
    const userIndex = rankings.findIndex((entry) => entry.userId === userId)
    return userIndex >= 0 ? userIndex + 1 : null
  } catch (error) {
    console.error('Error getting user rank:', error)
    return null
  }
}
